<script>
  import axios from "axios"
  import ApplicationState from "../../../Stores/applicationStates"
  import SinglePublish, { updateSinglePublish } from "../SinglePublish/Stores/singlePublishStore"
  import Palette from "../../Templates/Email/List/Modules/Palette/index.svelte"
  import { createEventDispatcher, onMount } from "svelte"
  import notify from "../../../Script/notify"
  import AuthInput from "../../../Shared/authInput.svelte"
  import LoadingScreen from "../SinglePublish/Components/loadingScreen.svelte"
  import Authentication, { updateAuth } from "./Stores/authStore"
  const dispatch = createEventDispatcher()

  /**
   * Boolean indicating whether the sign model is active or not.
   * Default value is false.
   */
  export let signModel = false

  /**
   * Boolean indicating the activation state of the position overlay button.
   * Default value is false.
   */
  export let activatePositionOverlayBtn = false

  /**
   * Variable to store the current page number.
   */
  export let currentpage

  /**
   * Variable to store the position of the PDF signature.
   */
  export let pdfPosition

  /**
   * Variable to store the background color of the signature.
   * Default value is "#ffffff" (white).
   */
  export let SignBgClr = "#ffffff"

  /**
   * Boolean toggle variable.
   * Default value is false.
   */
  export let toggle = false

  /**
   * Variable to track the active state of the sign model.
   * 0: Not active, 1: Active with sign details submitted, 2: Active with signed PDF downloaded.
   * Default value is 0.
   */
  export let signModelActive = 0

  /**
   * String variable to store the reason for signing.
   * Initialized as an empty string.
   */
  let reason = ""

  /**
   * Boolean variable indicating the visibility state of a toggle.
   * Default value is false.
   */
  let ShowToggle = false

  /**
   * Boolean variable indicating the state of sign initialization button.
   * Default value is false.
   */
  let signInitBtn = false

  /**
   * Maximum number of characters allowed for the reason string.
   * Default value is 120.
   */
  let maxCharacters = 120

  /**
   * Timeout variable used for certain operations.
   */
  let timeOut

  /**
   * Boolean variable indicating the state of signing load.
   * Default value is false.
   */
  let signingload = false

  /**
   * Variable to store the authentication code.
   * Initialized as an empty string.
   */
  let code = ""

  /**
   * @type {maxNotification}
   */
  let maxNotification = {
    /**
     * Closes the notification.
     */
    close() {},
  }

  /**
   * @type {notificationSplChar}
   */
  let notificationSplChar = {
    close() {},
  }

  /**
   * @type {AuthError}
   */
  let AuthError = {
    close() {},
  }

  /**
   * @type {confirmAuthError}
   */
  let confirmAuthError = {
    close() {},
  }

  /**
   * @type {formValidError}
   */
  let formValidError = {
    close() {},
  }

  /**
   * @type {RegExp} Regular expression to match allowed characters
   */
  let allowedCharsRegex = /^[A-Za-z0-9\s-()+&.]+$/
  let signerId
  onMount(async () => {
    try {
      confirmAuthError.close()
      // signerId = await fetchSignerId($ApplicationState.email)
      // console.log({ signerId })
    } catch (error) {
      // Modify error codes for better readability or handling
      // console.error(error);
      const info = "PAGE: Single Publish (Digital Signature)\nEVENT: onMount"
      confirmAuthError = notify.danger(error, info)
    }
  })

  const fetchSignerId = (email) => {
    return new Promise(async (resolve, reject) => {
      try {
        if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR" // Throw network error if offline

        // Send a POST request to validate the authentication code
        const { data } = await axios.get(`/api/pdf-signing/v2/internal/signers/by-email?email=${email}`)

        if (data.error && !data.success) {
          // Handle case where authentication code validation fails
          throw data.errorCode
        }
        resolve(data.signerId)
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Handles keypress events to validate and limit input characters for the reason field.
   * Displays notifications for exceeding character limits and disallowed characters.
   *
   * @param {Event} e - The keypress event object.
   */
  const checkMaxCharacters = async (e) => {
    if (reason.length >= maxCharacters) {
      // If the reason exceeds maxCharacters, prevent input and show notification
      e.preventDefault()
      reason += e.key // Append the key to reason temporarily
      maxNotification.close() // Close any existing max character notification

      // Delayed slice to truncate reason to maxCharacters length
      setTimeout(() => {
        reason = reason.slice(0, maxCharacters)
      }, 20)

      // Notify user about exceeding max character limit
      maxNotification = notify.info(`Cannot enter more than ${maxCharacters} characters`)
    }

    if (!allowedCharsRegex.test(e.key)) {
      // If the key is not allowed, prevent input and show notification
      e.preventDefault()
      reason += e.key // Append the key to reason temporarily
      notificationSplChar.close() // Close any existing special character notification

      // Show a danger notification about allowed characters
      notificationSplChar = notify.danger("Only letters, numbers, and special characters -()+&. are allowed.")

      // Delayed removal of the last character added
      setTimeout(() => {
        reason = reason.slice(0, -1)
      }, 100)
    }
  }

  /**
   * Handles form submission for signing the PDF.
   * Sends form data to the server for PDF signing, handles responses, and updates UI accordingly.
   */
  const formSubmit = async () => {
    clearTimeout(timeOut) // Clear any existing timeout
    AuthError.close() // Close any existing authentication error notification

    if (!$Authentication.token) {
      // If authentication token is missing, notify user to verify authentication
      AuthError = notify.danger("Please verify Authentication")
    } else {
      // Set signingload flag to true, indicating signing process is active
      signingload = true
      // Prepare form data for PDF signing
      const formData = {
        url: $SinglePublish.uri,
        reason: reason,
        signPage: String(currentpage - 1),
        signBGColor: SignBgClr,
        signPosition: pdfPosition,
        file: $SinglePublish.pdfFile,
        signField: `Signature-${new Date().getTime()}`,
      }

      try {
        // Set signInitBtn flag to true, indicating signing initialization in progress
        signInitBtn = true
        // Send a POST request to sign the PDF with form data
        // const { data } = await axios.post("/api/pdfsigner/v2/signature/sign", formData, {
        const { data } = await axios.post("/api/pdf-signing/v2/signature/sign", formData, {
          headers: {
            "access-token": $Authentication.token,
            "content-type": "multipart/form-data",
          },
        })

        if (data.error && !data.success) {
          // Handle case where PDF signing fails
          throw "PDF signing failed. Please try again."
        }

        // Notify user about successful PDF signing
        notify.success("The PDF has been successfully signed. Click the download button to proceed with the download.")

        // Update UI and data with signed PDF details
        signModelActive = 2
        $SinglePublish.signedPDFName = data.signedPDF
        $SinglePublish.txid = data.txid
        // $SinglePublish.downloadlink = "/api/pdfsigner/v2/download/" + data.signedPDF
        $SinglePublish.downloadlink = "/api/pdf-signing/v2/download/" + data.signedPDF

        // Download the signed PDF and update UI with download link
        // const response = await axios.get(`/api/pdfsigner/v2/download/${data.signedPDF}`, { responseType: "blob" })
        const response = await axios.get(`/api/pdf-signing/v2/download/${data.signedPDF}`, { responseType: "blob" })
        notify.info("Please wait <strong>signed PDF</strong> will be replaced")

        // Create a File object from the downloaded blob and update $SinglePublish
        const myFile = new File([response.data], "signedPdf.pdf", {
          type: "application/pdf",
        })
        $SinglePublish.pdfFile = myFile
        $SinglePublish.uri = $SinglePublish.downloadlink
        // Update UI with new PDF details
        updateSinglePublish($SinglePublish)
        // Dispatch an action to handle changes in signed PDF
        dispatch("changeIndoSignedPdf")
      } catch (error) {
        // Handle errors during PDF signing
        // console.error(error);
        AuthError = notify.danger(error.toString())
      } finally {
        // Set signingload flag to false after signing process completes or fails
        signingload = false
        // Set signInitBtn flag to false after signing initialization completes or fails
        signInitBtn = false
        // Clear authentication token
        $Authentication.token = ""
        // Update authentication state in the application
        updateAuth($Authentication)
      }
    }
  }

  /**
   * Handles confirmation of authentication code input.
   * Validates the code, sends it to the server for verification, and updates UI based on server response.
   *
   * @param {Event} e - The submit event object from the authentication form.
   */
  const confirmAuthCode = async (e) => {
    let target = e.detail.target

    // Construct the code from input fields
    code = target[0].value + target[1].value + target[2].value + target[3].value + target[4].value + target[5].value

    // Set a timeout to clear code and authentication token after 5 minutes (300,000 milliseconds)
    timeOut = setTimeout(() => {
      code = ""
      $Authentication.token = ""
      updateAuth($Authentication)
    }, 300000)

    confirmAuthError.close() // Close any existing authentication error notification

    try {
      // Prepare form data for authentication code confirmation
      const formData = {
        // signerId: "SIGNER007", // Example signer ID (can be dynamic or user-specific)
        signerId: signerId, // Example signer ID (can be dynamic or user-specific)
        code: code, // The authentication code to be validated
      }

      if (!window.navigator.onLine) throw "P2BCODE::NETWORK_ERROR" // Throw network error if offline

      // Send a POST request to validate the authentication code
      const { data } = await axios.post("/api/pdf-signing/v2/auth/token", formData)

      if (data.error && !data.success) {
        // Handle case where authentication code validation fails
        throw data.errorCode
      }

      // Update authentication token with the received token from the server
      $Authentication.token = data.token
      updateAuth($Authentication) // Update authentication state in the application

      // Notify user about successful authentication completion
      notify.success("Your Authentication process successfully completed.")
    } catch (error) {
      code = "" // Clear the code input
      if (error.code == "ERR_BAD_RESPONSE") {
        // Handle specific error code for bad response from server
        confirmAuthError = notify.danger("We've encountered an error on our end. Please try again after some time.")
      } else {
        // Modify error codes for better readability or handling
        // console.error(error);
        confirmAuthError = notify.danger(error) // Display error notification for other errors
      }
    }
  }

  /**
   * Handles submission of sign details.
   * Validates required fields (reason and pdfPosition) and triggers appropriate actions based on validation results.
   */
  const submitSignDetails = () => {
    formValidError.close() // Close any existing form validation error notification

    if (reason && pdfPosition) {
      // If reason and pdfPosition are both provided, proceed to next sign model state
      signModelActive = 1
      dispatch("removeBlueLayer") // Dispatch action to remove blue layer or perform related UI update
    } else if (!pdfPosition) {
      // If pdfPosition is not selected, show a form validation error
      formValidError = notify.danger("Choose sign position")
    } else if (!reason) {
      // If reason is empty, show a form validation error
      formValidError = notify.danger("Reason can't be empty")
    }
  }
</script>

<svelte:head>
  <title>Document Signing</title>
</svelte:head>

<div class="relative flex h-screen w-full flex-col justify-between overflow-auto p-6 dark:bg-black sm:h-full">
  {#if signModelActive == 0}
    <div>
      <div class="flex flex-col gap-3">
        <button
          on:click={() => {
            reason = ""
            pdfPosition = ""
            signModel = false
            signModelActive = 0
            dispatch("changeIndoSignedPdf")
          }}
          class="absolute right-3 top-3 text-gray-500 hover:text-red-600 active:text-red-400"
        >
          <i class="fa-solid fa-circle-xmark" />
        </button>
        <h1 class="mb-3 select-none text-2xl font-bold dark:text-white">Document Signature</h1>
        <p class="mb-6 w-4/5 select-none text-gray-200">{signModelActive == 2 ? " " : "Sign the document by using the given below details."}</p>
      </div>
      <div class="flex flex-col items-start gap-3">
        <h1 class="mb-3 select-none text-lg font-bold dark:text-white">Signing Details</h1>
        <div class="w-full md:space-y-3">
          <div class="w-full rounded-md p-3">
            <Palette bind:headerBackground={SignBgClr} colorCount={18} disableGradient={true} BtnName="Choose Sign Background Color" position="bottom" />
          </div>
          <div class="w-full space-y-3 rounded-md p-3">
            <button
              bind:this={activatePositionOverlayBtn}
              id="signBtn"
              on:click={() => {
                signInitBtn = false
                ShowToggle = true
                dispatch("signPlacement")
              }}
              type="button"
              class="flex h-9 {pdfPosition ? 'border-[#a0a1a580]  text-gray-900 ' : 'animate-shake border-red-500 text-red-500'} w-full cursor-pointer items-center justify-center gap-2 rounded-md border bg-[#f7f7f7] px-2 text-center text-sm font-medium hover:bg-blue-400 hover:text-white focus:outline-none active:bg-blue-500 active:text-white disabled:cursor-not-allowed dark:bg-lightgray dark:text-white dark:hover:bg-blue-500"
            >
              <i class="fa-solid fa-arrows-up-down-left-right" />
              <span class="select-none">&nbsp;&nbsp;Select Sign Placement</span>
            </button>
            <div class="mb-3 items-center gap-3 {ShowToggle ? 'flex' : 'hidden'}">
              <button
                on:click={() => {
                  toggle = !toggle
                  dispatch("signPlacement", toggle)
                }}
                class="flex w-11 items-center {!toggle ? 'justify-start' : 'justify-end'}  rounded-full border border-[#a0a1a580] bg-[#f7f7f7] p-1 dark:bg-lightgray"
              >
                <div class="h-4 w-4 rounded-full {!toggle ? 'bg-gray-200' : ' bg-blue-500'}" />
              </button>
              <p class="select-none font-semibold {!toggle ? 'text-gray-500' : ' text-blue-500'} ">Horizontaly Lock</p>
            </div>
          </div>
          <div class="w-full space-y-3 rounded-md p-3">
            <p class="block w-full select-none text-base font-medium text-gray-200">Reason for Digital signature</p>
            <textarea
              required
              placeholder="Enter reason"
              on:blur={(e) => {
                checkMaxCharacters(e)
                reason = e.target.value.trim()
              }}
              on:keypress={checkMaxCharacters}
              bind:value={reason}
              type="text"
              name="reason"
              class="block max-h-[200px] min-h-[80px] w-full resize-none appearance-none rounded-md border border-[#a0a1a580] bg-transparent px-3 py-2 pl-5 text-sm text-gray-800 placeholder-gray-200 outline-none invalid:animate-shake invalid:border-red-500 invalid:placeholder-red-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:invalid:border-red-500 focus:invalid:ring-red-500 dark:text-white"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-end">
      <button on:click={() => submitSignDetails()} class=" {!reason || !pdfPosition ? 'cursor-not-allowed bg-blue-300 hover:bg-blue-400 active:bg-blue-200 ' : 'bg-blue-500 hover:bg-blue-600 active:bg-blue-400'} flex h-9 w-auto min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] px-2 text-center text-sm font-medium text-white hover:text-white focus:outline-none md:w-32 lg:px-4">
        Next&nbsp;&nbsp;
        <i class="fa-solid fa-chevron-right" />
      </button>
    </div>
  {:else if signModelActive == 1}
    <div>
      <div class="flex flex-col gap-3">
        <button
          on:click={() => {
            reason = ""
            pdfPosition = ""
            signModel = false
            signModelActive = 0
            dispatch("changeIndoSignedPdf")
          }}
          class="absolute right-3 top-3 text-gray-500 hover:text-red-600 active:text-red-400"
        >
          <i class="fa-solid fa-circle-xmark" />
        </button>
        <h1 class="mb-3 select-none text-2xl font-bold dark:text-white">Document Signature</h1>
        <p class="mb-6 w-4/5 select-none text-gray-200">{signModelActive == 2 ? " " : "Sign the document by using the given below details."}</p>
      </div>
      <div class="flex flex-col items-start gap-3">
        <h1 class="mb-3 select-none text-lg font-bold dark:text-white">Signer Authenticate</h1>
        <div class="w-full space-y-3 rounded-md p-3">
          <!-- <Dropdown
          on:itemSelect={(e) => {
            selected = e.detail
            // authInput1.focus()
          }}
          {dropdownItems}
          {selected}
        /> -->
          <div class="w-full select-none rounded-md border border-[#a0a1a580] bg-[#f7f7f7] px-3 py-2 hover:bg-[#f7f7f7] dark:bg-darkgray">
            <div class="flex items-center gap-2">
              <div class="select-none truncate text-sm font-bold dark:text-gray-200">{$ApplicationState.name}</div>
              <div class="text-xs font-semibold text-gray-500">({$ApplicationState.issuerID})</div>
            </div>
            <h1 class="text-sm dark:text-gray-200">{$ApplicationState.email}</h1>
          </div>
        </div>
        {#if $Authentication.token}
          <div class="flex w-full justify-center">
            <div class="flex min-h-[144px] select-none items-center justify-center p-3">
              <div class="flex h-full w-full flex-col items-center justify-center rounded-md py-2 text-green-500">
                <div class="flex h-28 w-28 items-center justify-center rounded-full bg-[#5cb85c]/30">
                  <div class="circle1 flex h-24 w-24 items-center justify-center rounded-full bg-[#5cb85c]/50">
                    <div class="circle2 flex h-[70px] w-[70px] items-center justify-center rounded-full bg-[#5cb85c]">
                      <div class="tick text-5xl text-white">
                        <i class="fa-solid fa-check" />
                      </div>
                    </div>
                  </div>
                </div>
                <span class="text-extrabold mt-6 text-center text-3xl">Authenticated</span>
              </div>
            </div>
          </div>
        {:else}
          <AuthInput btnName="Authenticate" bind:code on:submit={confirmAuthCode} />
        {/if}
      </div>
    </div>

    <div class="flex items-center justify-between">
      <button on:click={() => (signModelActive = 0)} type="button" class="flex h-9 w-auto min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] bg-[#f7f7f7] px-2 text-sm font-medium text-gray-900 hover:bg-white focus:outline-none focus-visible:bg-white active:bg-[#f7f7f7] dark:bg-lightgray dark:text-white dark:hover:bg-bodyblack md:w-32 lg:px-4">
        <i class="fa-solid fa-chevron-left" />
        <span class="select-none md:block">&nbsp;&nbsp;Previous&nbsp;</span>
      </button>
      <button disabled={signInitBtn} on:click={formSubmit} type="button" class="{!$Authentication.token ? 'cursor-not-allowed bg-blue-300 hover:bg-blue-400 active:bg-blue-200' : 'bg-blue-500 hover:bg-blue-600 active:bg-blue-500'} flex h-9 w-auto min-w-[36px] items-center justify-center gap-2 rounded-md border border-[#a0a1a580] px-2 text-center text-sm font-medium text-white hover:text-white focus:outline-none disabled:bg-blue-300 disabled:hover:bg-blue-400 disabled:active:bg-blue-200 md:w-32 lg:px-4">
        <i class="fa-solid fa-pen-nib" />
        &nbsp;&nbsp;Sign PDF
      </button>
    </div>
  {:else if signModelActive == 2}
    <div class="flex w-full flex-col items-center">
      <div class="flex flex-col gap-3">
        <button
          on:click={() => {
            reason = ""
            pdfPosition = ""
            signModel = false
            signModelActive = 0
            dispatch("changeIndoSignedPdf")
          }}
          class="absolute right-3 top-3 text-gray-500 hover:text-red-600 active:text-red-400"
        >
          <i class="fa-solid fa-circle-xmark" />
        </button>
        <h1 class="mb-3 select-none text-2xl font-bold dark:text-white">Document Signature</h1>
        <p class="mb-6 w-4/5 select-none text-gray-200">{signModelActive == 2 ? " " : "Sign the document by using the given below details."}</p>
      </div>

      <div class="flex w-full select-none justify-center">
        <div class="mt-24 flex flex-col items-center justify-center gap-3 py-2 text-green-500">
          <div class="flex h-28 w-28 items-center justify-center rounded-full bg-[#5cb85c]/30">
            <div class="circle1 flex h-24 w-24 items-center justify-center rounded-full bg-[#5cb85c]/50">
              <div class="circle2 flex h-[70px] w-[70px] items-center justify-center rounded-full bg-[#5cb85c]">
                <div class="tick text-5xl text-white">
                  <i class="fa-solid fa-check" />
                </div>
              </div>
            </div>
          </div>

          <span class="text-extrabold mt-6 text-center text-3xl">Process Completed</span>
          <p class="text-center text-sm text-gray-800 dark:text-gray-200">The successful completion of the PDF signing process is confirmed. You can access the signed PDF for download by clicking here. Alternatively, you may proceed with the publishing process.</p>
        </div>
      </div>
    </div>
    <div class="flex items-center justify-between">
      <a href={$SinglePublish.downloadlink} target="_blank" class="flex h-9 w-auto min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] bg-blue-500 px-2 text-sm font-medium text-white hover:bg-blue-600 hover:text-white focus:outline-none active:bg-blue-500 disabled:cursor-not-allowed md:w-32 lg:px-4">
        <i class="fa-solid fa-download" />
        <span class="select-none">&nbsp;&nbsp;Download</span>
      </a>
      <button
        on:click={() => {
          reason = ""
          pdfPosition = ""
          code = ""
          signModel = false
          dispatch("changeIndoSignedPdf")
        }}
        type="button"
        class="flex h-9 w-auto min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] bg-blue-500 px-2 text-sm font-medium text-white hover:bg-blue-600 hover:text-white focus:outline-none active:bg-blue-500 disabled:cursor-not-allowed md:w-32 lg:px-4"
      >
        <span class="select-none md:block">Next&nbsp;&nbsp;</span>
        <i class="fa-solid fa-chevron-right" />
      </button>
    </div>
  {/if}

  {#if signingload}
    <LoadingScreen msg={"PDF Signing"} />
  {/if}
</div>

<style>
  ::-webkit-scrollbar-thumb {
    background: #f7f7f7;
    border-radius: 8px;
  }
  @keyframes spinner-animation {
    0% {
      width: 96px;
      height: 96px;
      fill-opacity: 1;
    }

    100% {
      width: 80px;
      height: 80px;
      fill-opacity: 0.5;
    }
  }
  @keyframes spinner {
    0% {
      width: 70px;
      height: 70px;
      fill-opacity: 1;
    }

    100% {
      width: 60px;
      height: 60px;
      fill-opacity: 0.5;
    }
  }
  @keyframes tick {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(0.5);
    }
  }
  .circle1 {
    animation: spinner-animation 0.5s 0.15s alternate infinite ease-in-out;
  }

  .circle2 {
    animation: spinner 0.5s 0.3s alternate infinite ease-in-out;
  }
  .tick {
    animation: tick 0.5s 0.15s alternate infinite ease-in-out;
  }
</style>
